import { LoginCallback } from '@okta/okta-react';
import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureFlagsQuery } from '@/hooks/queries/use-feature-flags';
import { Layout } from '@/Layout';
import { SecureRoute } from '@/router/SecureRouter';

const Login = lazy(() => import('@/components/Login'));
const CapitalGainsExtractor = lazy(() => import('@/pages/CapitalGainsExtractor'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const DocumentSplitter = lazy(() => import('@/pages/DocumentSplitter'));
const Extractors = lazy(() => import('@/pages/Extractors'));
const PayslipExtractor = lazy(() => import('@/pages/PayslipExtractor'));
const Staff = lazy(() => import('@/pages/Staff'));
const StorageBrowser = lazy(() => import('@/pages/StorageBrowser'));

// Gates the file browser behind the `tax_file_browser` flag. The flags query is
// disabled until an org is selected, so wait on it before deciding to avoid a
// premature redirect.
function StorageRoute() {
  const { selectedOrganizationId } = useOrganization();
  const { data, isPending } = useFeatureFlagsQuery();
  if (!selectedOrganizationId || isPending) {
    return null;
  }
  if (!data?.flags.tax_file_browser) {
    return <Navigate replace to="/dashboard" />;
  }
  return <StorageBrowser />;
}

export const AppRouter = () => {
  return (
    <Router>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-gray-900 border-b-2" />
          </div>
        }
      >
        <Routes>
          <Route element={<Login />} path="/login" />
          <Route element={<LoginCallback />} path="/login/callback" />
          <Route
            element={
              <SecureRoute>
                <Layout />
              </SecureRoute>
            }
            path="/"
          >
            <Route element={<Navigate replace to="/dashboard" />} index />
            <Route element={<Dashboard />} path="dashboard" />
            <Route path="extractors">
              <Route element={<Extractors />} index />
              <Route element={<PayslipExtractor />} path="payslip" />
              <Route element={<CapitalGainsExtractor />} path="capital-gains" />
              <Route element={<DocumentSplitter />} path="document-splitter" />
            </Route>
            <Route element={<Staff />} path="staff" />
            <Route element={<StorageRoute />} path="storage" />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};
